import React, { createRef, useCallback, useEffect, useContext, useState, useRef } from 'react';
import Link from 'next/link';
import clsx from 'clsx';

//styles
import styles from './mega-nav.module.scss';

import { NavContext } from 'Context/nav-context';
import { assetsPath, KEY } from 'Data/constants';
import { CMSData } from '@/types/cmsData';
import { useIntl } from 'react-intl';
import { formatLink } from '@/utils';
import { useDetermineBanner, useDetermineEnvironment } from '@/hooks';

type MenuItemL2L3 = {
  name: string;
  uid: string;
  link?: string | null;
  isL2?: boolean;
  index?: number;
  navL2L3Total?: number;
  megaNavL2Refs?: React.MutableRefObject<React.RefObject<HTMLAnchorElement>[][]>;
  hideName?: boolean;
  hasChildren?: boolean | null;
  navPromo?: {
    imageLink?: string;
    imageUrl: string;
    altName: string;
    name: string;
  };
};

const menuItemL2L3 = (
  { name, uid, link, isL2, navPromo }: MenuItemL2L3,
  index: number,
  navL2L3Total: number,
  navL2L3Length: number,
  megaNavL2Refs: React.MutableRefObject<React.RefObject<HTMLAnchorElement>[][]>,
  isPhxBanner: boolean,
  env: number,
  hideName?: boolean
) => {
  if (!link) link = '#main-content';

  return (
    <li
      className={clsx(styles.navItem, isL2 && styles.L2, uid, navL2L3Length > 1 && styles.addPaddingBottom)}
      key={uid}
      id={uid}
      role="none"
    >
      {navPromo ? (
        <>
          <a
            href={formatLink(navPromo.imageLink, isPhxBanner, env)}
            ref={megaNavL2Refs.current[index][navL2L3Total]}
            className={clsx(styles.navPromo, styles.noBorderBottom, styles.noTextDecoration)}
          >
            <img
              src={navPromo.imageUrl}
              alt={navPromo.altName || navPromo.name}
              aria-label={navPromo.altName || navPromo.name}
              width={100}
              height={24}
            />
            {name && !hideName ? <span>{name}</span> : null}
          </a>
        </>
      ) : (
        <a href={formatLink(link, isPhxBanner, env)} ref={megaNavL2Refs.current[index][navL2L3Total]} role="menuitem">
          {name}
        </a>
      )}
    </li>
  );
};

const MegaNav = ({
  navigation = [],
  focusRef,
}: {
  navigation?: CMSData[];
  focusRef?: React.MutableRefObject<HTMLElement | null>;
}) => {
  const { formatMessage } = useIntl();
  const isPhxBanner = useDetermineBanner();
  const env = useDetermineEnvironment();

  const {
    category: {
      ['uid']: [uid],
      ['L0']: [L0, setL0],
      ['L1']: [L1, setL1],
    },
    ['nav']: {
      isMobileNavOpen: [isMobileNavOpen],
    },
  } = useContext(NavContext);

  const [currentIndexL1, setIndexL1] = useState(0);
  const [currentIndexL2, setIndexL2] = useState(0);

  const navL0 = navigation?.filter((x) => x.uid === uid)[0]?.child?.filter((y) => y.uid === L0)[0];
  const navL1 = navL0?.child;
  const showNav =
    L0 === '' || L0 === 'FrontShopOffersNavNode' || !navL0?.hasChildren || isMobileNavOpen
      ? { display: 'none' }
      : { display: 'block' };

  const navL1ArrLength = navL1?.length;

  let megaNavL1Refs = useRef<React.RefObject<HTMLAnchorElement>[]>([]);
  let megaNavL2Refs = useRef<React.RefObject<HTMLAnchorElement>[][]>([]);

  // normalize and flatten L2 and L3 items into one array for each L2, not all items
  const navL2L3 = navL1?.map((L1) => {
    return L1.child?.map(({ name, uid, link, child, hasChildren }) => {
      return !hasChildren
        ? [{ name, uid, link, hasChildren, isL2: true }]
        : [{ name, uid, link, hasChildren, isL2: true }, ...child!];
    });
  });

  megaNavL1Refs.current = Array(navL1ArrLength)
    .fill(null)
    .map((_, indexL1) => megaNavL1Refs.current[indexL1] ?? createRef());

  if (navL2L3) {
    megaNavL2Refs.current = Array(navL1ArrLength)
      .fill(null)
      .map((_, indexL1) => {
        return Array(navL2L3[indexL1]?.flat()?.length)
          .fill(null)
          .map((_, indexL2) => megaNavL2Refs?.current?.[indexL1]?.[indexL2] ?? createRef());
      });
  }

  const onLeave = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | React.FocusEvent<HTMLElement, Element>,
    setL0: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const relatedTarget = (
      (e as React.FocusEvent<HTMLElement, Element>).relatedTarget?.parentNode?.parentNode
        ?.parentNode as HTMLElement | null
    )?.className;

    if (
      !relatedTarget?.includes('mega') &&
      !relatedTarget?.includes('L1') &&
      !relatedTarget?.includes('L2') &&
      !relatedTarget?.includes('shopbyfeatured')
    ) {
      setL0('');
    }
  };

  const closeMegaNav = useCallback(() => {
    setL0('');
    setL1('');
    setIndexL1(0);
    setIndexL2(0);
    if (typeof window !== 'undefined') {
      if (document?.activeElement?.className !== 'L0') focusRef?.current?.focus();
    }
  }, [focusRef, setL0, setL1]);

  const keydownHandler = useCallback(
    (event: KeyboardEvent) => {
      event = event || window.event;
      if (event.key !== KEY.ENTER) event.preventDefault();

      const key = event.key === KEY.TAB && event.shiftKey ? 'Shift' + event.key : event.key;

      switch (key) {
        case KEY.ESCAPE:
          closeMegaNav();
          break;
        case KEY.ARROW_LEFT:
          megaNavL1Refs.current[currentIndexL1]?.current?.focus();
          break;
        case 'Shift' + KEY.TAB:
        case KEY.ARROW_UP:
          if (document?.activeElement?.className === 'L1') {
            if (currentIndexL1 !== 0) {
              setIndexL1(currentIndexL1 - 1);
              setIndexL2(0);
              megaNavL1Refs.current[currentIndexL1 - 1]?.current?.focus();
            } else if (currentIndexL1 == 0) {
              closeMegaNav();
              focusRef?.current?.focus();
            }
          } else {
            if (currentIndexL2 !== 0) {
              setIndexL2(currentIndexL2 - 1);
              megaNavL2Refs.current[currentIndexL1][currentIndexL2 - 1]?.current?.focus();
            }
          }
          break;
        case KEY.ARROW_RIGHT:
          if (document?.activeElement?.className === 'L1') {
            megaNavL2Refs.current[currentIndexL1][currentIndexL2]?.current?.focus();
          }
          break;
        case KEY.TAB:
        case KEY.ARROW_DOWN:
          if (document?.activeElement?.className === 'L0') {
            megaNavL1Refs.current[0]?.current?.focus();
          } else if (document?.activeElement?.className === 'L1') {
            if (currentIndexL1 < megaNavL1Refs.current.length - 1) {
              setIndexL1(currentIndexL1 + 1);
              setIndexL2(0);
              megaNavL1Refs.current[currentIndexL1 + 1]?.current?.focus();
            }
          } else {
            if (currentIndexL2 < megaNavL2Refs.current[currentIndexL1].length - 1) {
              setIndexL2(currentIndexL2 + 1);
              megaNavL2Refs.current[currentIndexL1][currentIndexL2 + 1]?.current?.focus();
            }
          }
          break;
        case KEY.HOME:
          if (document?.activeElement?.className === 'L1') {
            setIndexL1(0);
            megaNavL1Refs.current[0]?.current?.focus();
          } else {
            setIndexL2(0);
            megaNavL2Refs.current[currentIndexL1][0]?.current?.focus();
          }
          break;
        case KEY.END:
          if (document?.activeElement?.className === 'L1') {
            setIndexL1(megaNavL1Refs.current.length - 1);
            megaNavL1Refs.current[megaNavL1Refs.current.length - 1]?.current?.focus();
          } else {
            setIndexL2(megaNavL2Refs.current[currentIndexL1].length - 1);
            megaNavL2Refs.current[currentIndexL1][megaNavL2Refs.current[currentIndexL1].length - 1]?.current?.focus();
          }
          break;
      }
    },
    [focusRef, closeMegaNav, currentIndexL1, currentIndexL2, setIndexL1, setIndexL2, megaNavL1Refs, megaNavL2Refs]
  );

  useEffect(() => {
    if (L0) window.addEventListener('keydown', keydownHandler);

    return () => {
      window.removeEventListener('keydown', keydownHandler);
    };
  }, [L0, keydownHandler, navL1ArrLength, navL2L3]);

  return (
    <nav
      className={styles.megaNavContainer}
      style={showNav}
      onMouseLeave={(e) => onLeave(e, setL0)}
      onBlur={(e) => onLeave(e, setL0)}
      data-testid={'mega-navigation'}
    >
      <div className={styles.navHeaderLayout}>
        <div className={styles.L1Container}>
          <ul className={styles.navItemsLayout} role="menu" aria-label={formatMessage({ id: 'beauty' })}>
            {navL1?.map(({ name, uid, link }, index) => {
              return (
                <li
                  className={clsx(uid === L1 && styles.selectedNav, uid)}
                  key={uid}
                  onMouseEnter={() => {
                    setL1(uid);
                  }}
                  onFocus={() => {
                    setL1(uid);
                  }}
                  role="none"
                >
                  <a
                    href={formatLink(link ? link : '/all-brands', isPhxBanner, env)}
                    className="L1"
                    ref={megaNavL1Refs.current[index]}
                    role="menuitem"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    {name}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
        <div className={styles.L2Container}>
          {navL1?.map((navL1, index) => {
            let navL2L3Total = -1;
            return (
              <div className={clsx(navL1.uid === L1 && styles.showL2Container)} key={navL1.uid} id={navL1.uid}>
                <div
                  className={clsx(
                    styles.L2L3Container,
                    navL1.uid === 'FrontShopBeautyShopByBrandsNavNode' && styles.flexContainer
                  )}
                >
                  {navL2L3?.[index]?.map((item) => {
                    item = item.flat();
                    if (!item || !item.length) return;
                    if (item[0].uid.includes('ShopBy') || item[0].uid.includes('Featured')) return;
                    const showSeeAllBrands = item[0]?.uid.includes('FrontShopBeautyBrandsPopularBrandsNavNode');
                    return (
                      <React.Fragment key={item[0].uid}>
                        <ul
                          className={clsx(item.length === 1 && styles.L2)}
                          key={item[0]?.uid}
                          id={item[0]?.uid}
                          role="menu"
                        >
                          {item.map((nodeL2L3) => {
                            navL2L3Total += 1;
                            return menuItemL2L3(
                              nodeL2L3,
                              index,
                              navL2L3Total,
                              item.length,
                              megaNavL2Refs,
                              isPhxBanner,
                              env,
                              true
                            );
                          })}
                        </ul>
                        {showSeeAllBrands ? (
                          <ul key="SeeAllBrands" id={styles.SeeAllBrands} role="menu">
                            <li role="none">
                              <Link href={formatLink('/all-brands', isPhxBanner, env)} role="menuitem">
                                <span className={styles.SeeAllBrands}>{formatMessage({ id: 'seeAllBrands' })}</span>{' '}
                                <span className={styles.carat}>
                                  <img
                                    src={`${assetsPath}/site-icons/arrow-black-new.svg`}
                                    alt={formatMessage({ id: 'seeAllBrands' })}
                                    width={100}
                                    height={24}
                                  />
                                </span>
                              </Link>
                            </li>
                          </ul>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className={styles.shopbyfeatured}>
                  {navL2L3?.[index]?.map((item) => {
                    item = item.flat();
                    if (!item[0]?.uid?.includes('ShopBy') && !item[0]?.uid?.includes('Featured')) return;
                    if (!item[0]?.hasChildren) return;
                    return (
                      <React.Fragment key={item[0]?.uid}>
                        {
                          <ul
                            className={clsx(
                              item?.length === 1 && styles.L2,
                              item[0]?.uid?.includes('ShopBy') && styles.shopby,
                              item[0]?.uid?.includes('Featured') && styles.featured
                            )}
                            key={item[0]?.uid}
                            id={item[0]?.uid}
                          >
                            {item.map((nodeL2L3) => {
                              if (
                                !nodeL2L3?.hasChildren &&
                                nodeL2L3?.uid?.includes('ShopBy') &&
                                nodeL2L3?.uid?.includes('Featured')
                              ) {
                                return;
                              }

                              navL2L3Total += 1;
                              return menuItemL2L3(
                                nodeL2L3,
                                index,
                                navL2L3Total,
                                item.length,
                                megaNavL2Refs,
                                isPhxBanner,
                                env
                              );
                            })}
                          </ul>
                        }
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default MegaNav;
